/**
 * 달 표면 — cartoon 크레이터 텍스처.
 *
 * 은빛 회색 base 위에 hand-drawn 크레이터 dot 들을 흩뿌린다. 각 dot 은:
 *   - 어두운 안쪽 (충돌 흔적)
 *   - 밝은 위쪽 rim arc (햇빛 각도 표현)
 *   - 큰 크레이터 + 작은 크레이터 두 단계 크기
 *
 * 추가로 부드러운 radial vignette 으로 입체감을 살린다. 모든 그리기는
 * Canvas2D primitives — 외부 asset 없이 cartoon 스타일 일관성 유지.
 *
 * happy-dom (테스트 환경) 에서는 Canvas2D context 가 null 이므로 빈
 * 회색 DataTexture fallback 을 반환.
 */

import {
  CanvasTexture,
  DataTexture,
  LinearFilter,
  RGBAFormat,
  type Texture,
} from "three";

const DEFAULT_SIZE = 512;
const BASE_COLOR = "#c4c0d2";   // silver-violet base
const DEEP_COLOR = "#9893a8";   // recessed regions
const CRATER_SHADOW = "#5d5969"; // crater pit
const CRATER_RIM = "#e8e4f4";    // bright rim arc

interface CraterSpec {
  /** Center in normalized [0..1]. */
  cx: number;
  cy: number;
  /** Radius in normalized [0..1]. */
  r: number;
  /** Whether to draw a bright rim arc. */
  hasRim: boolean;
}

// Hand-placed cartoon craters — sized like real lunar maria + small
// impact craters. Mix of sizes for visual rhythm.
const CRATERS: ReadonlyArray<CraterSpec> = [
  { cx: 0.30, cy: 0.34, r: 0.085, hasRim: true },
  { cx: 0.62, cy: 0.28, r: 0.070, hasRim: true },
  { cx: 0.48, cy: 0.55, r: 0.050, hasRim: true },
  { cx: 0.22, cy: 0.66, r: 0.060, hasRim: true },
  { cx: 0.78, cy: 0.62, r: 0.040, hasRim: true },
  { cx: 0.40, cy: 0.78, r: 0.045, hasRim: true },
  { cx: 0.66, cy: 0.46, r: 0.030, hasRim: false },
  { cx: 0.18, cy: 0.20, r: 0.025, hasRim: false },
  { cx: 0.55, cy: 0.18, r: 0.022, hasRim: false },
  { cx: 0.82, cy: 0.36, r: 0.028, hasRim: false },
  { cx: 0.32, cy: 0.50, r: 0.020, hasRim: false },
  { cx: 0.72, cy: 0.78, r: 0.022, hasRim: false },
  { cx: 0.86, cy: 0.18, r: 0.018, hasRim: false },
  { cx: 0.10, cy: 0.45, r: 0.018, hasRim: false },
  { cx: 0.55, cy: 0.85, r: 0.020, hasRim: false },
];

function paintBase(ctx: CanvasRenderingContext2D, size: number): void {
  // Soft two-tone fill: BASE_COLOR with subtle DEEP_COLOR splotches as
  // recessed plains (mare-like) — gives the surface visual rhythm
  // before craters sit on top.
  ctx.fillStyle = BASE_COLOR;
  ctx.fillRect(0, 0, size, size);

  const splotches: ReadonlyArray<readonly [number, number, number]> = [
    [0.45, 0.40, 0.20],
    [0.65, 0.62, 0.16],
    [0.25, 0.72, 0.14],
  ];
  ctx.fillStyle = DEEP_COLOR;
  for (const [u, v, r] of splotches) {
    ctx.beginPath();
    ctx.ellipse(u * size, v * size, r * size, r * size * 0.85, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

function paintCrater(
  ctx: CanvasRenderingContext2D,
  size: number,
  spec: CraterSpec,
): void {
  const cx = spec.cx * size;
  const cy = spec.cy * size;
  const r = spec.r * size;
  // Pit body — flat dark fill.
  ctx.fillStyle = CRATER_SHADOW;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  // Subtle dark ring outline so the pit reads against the silver base.
  ctx.strokeStyle = "rgba(20, 18, 28, 0.45)";
  ctx.lineWidth = Math.max(1, r * 0.10);
  ctx.stroke();
  // Bright rim arc on the upper-left — implies sun overhead-left.
  if (spec.hasRim) {
    ctx.strokeStyle = CRATER_RIM;
    ctx.lineWidth = Math.max(1.3, r * 0.18);
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.96, Math.PI * 1.05, Math.PI * 1.85);
    ctx.stroke();
  }
}

function paintVignette(ctx: CanvasRenderingContext2D, size: number): void {
  // Soft radial darken toward edges — bias slightly toward bottom-right
  // to suggest a single distant light source.
  const grad = ctx.createRadialGradient(
    size * 0.32, size * 0.32, size * 0.10,
    size * 0.62, size * 0.62, size * 0.85,
  );
  grad.addColorStop(0.00, "rgba(255, 255, 255, 0.10)");
  grad.addColorStop(0.55, "rgba(0, 0, 0, 0.00)");
  grad.addColorStop(1.00, "rgba(0, 0, 0, 0.30)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
}

export interface PaintMoonSurfaceOptions {
  size?: number;
}

export function paintMoonSurface(opts: PaintMoonSurfaceOptions = {}): Texture {
  const size = opts.size ?? DEFAULT_SIZE;
  if (typeof document !== "undefined") {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      paintBase(ctx, size);
      for (const c of CRATERS) paintCrater(ctx, size, c);
      paintVignette(ctx, size);
      const tex = new CanvasTexture(canvas);
      tex.minFilter = LinearFilter;
      tex.magFilter = LinearFilter;
      return tex;
    }
  }
  // headless fallback — flat silver pixel buffer (Texture mounts but
  // craters won't render in tests).
  const data = new Uint8Array(size * size * 4);
  for (let i = 0; i < data.length; i += 4) {
    data[i] = 0xc4;
    data[i + 1] = 0xc0;
    data[i + 2] = 0xd2;
    data[i + 3] = 0xff;
  }
  const tex = new DataTexture(data, size, size, RGBAFormat);
  tex.needsUpdate = true;
  return tex;
}

export const __testing = {
  CRATERS,
  DEFAULT_SIZE,
  BASE_COLOR,
  CRATER_SHADOW,
};
